import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import TeamSearchInput from '../components/TeamSearchInput';
import BudgetInput from '../components/BudgetInput';
import AdSense, { ADSENSE_SLOTS } from '../components/ads/AdSense';
import './TeamSelection.css';

function TeamSelection() {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [customTeamName, setCustomTeamName] = useState('');
  const [budget, setBudget] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 900;
  const isSmallMobile = windowWidth <= 480;

  const handleTeamSelect = (team) => {
    if (team.nation === 'Custom') {
      setSelectedTeam(null);
      setCustomTeamName('');
    } else {
      setSelectedTeam(team);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let teamName = '';
    if (selectedTeam && selectedTeam.nation !== 'Custom') {
      teamName = selectedTeam.name;
    } else if (customTeamName.trim()) {
      teamName = customTeamName;
    } else {
      setErrorMsg('Please select or enter a team name.');
      return;
    }

    const parsedBudget = parseFloat(budget);
    if (isNaN(parsedBudget) || parsedBudget === 0) {
      setErrorMsg('Please enter a valid transfer budget.');
      return;
    }
    if (parsedBudget > 10000) {
      setErrorMsg('Budget cannot exceed £10,000,000,000.');
      return;
    }

    setErrorMsg('');
    localStorage.removeItem('rosterData');
    localStorage.removeItem('buyList');
    localStorage.removeItem('savedFormation');
    localStorage.removeItem('currentLineup');
    localStorage.setItem('restartLineup', 'true');
    localStorage.setItem('selectedTeam', teamName);
    localStorage.setItem('transferBudget', parsedBudget);
    localStorage.removeItem('skippedToTransferBuilder');

    navigate('/roster-management');
  };

  const hasTeamName =
    (selectedTeam && selectedTeam.nation !== 'Custom') ||
    customTeamName.trim().length > 0;
  const hasBudget = budget !== '' && parseFloat(budget) > 0;
  const isFormValid = hasTeamName && hasBudget;

  return (
    <main className="ts-main">
      <Helmet>
        <title>Free Football Transfer Builder | Plan Signings &amp; Sales</title>
        <meta
          name="description"
          content="Plan your club's transfer window for free. Choose a team and budget, buy and sell players, rebuild the squad, create a lineup and share your result."
        />
        <link rel="canonical" href="https://www.squadverse.uk/transfer-builder-setup" />
        <meta property="og:title" content="Free Football Transfer Builder | Squadverse" />
        <meta
          property="og:description"
          content="Choose a club and budget, plan signings and sales, build your final squad and share the result."
        />
        <meta
          property="og:image"
          content="https://www.squadverse.uk/og/transfer-builder.png"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.squadverse.uk/transfer-builder-setup" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Football Transfer Builder | Squadverse" />
        <meta name="twitter:description" content="Choose a club and budget, plan signings and sales, build your final squad and share the result." />
        <meta name="twitter:image" content="https://www.squadverse.uk/og/transfer-builder.png" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, user-scalable=yes"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'Squadverse Football Transfer Builder',
            url: 'https://www.squadverse.uk/transfer-builder-setup',
            applicationCategory: 'SportsApplication',
            operatingSystem: 'Any',
            browserRequirements: 'Requires JavaScript and a modern web browser',
            description: 'A free football transfer planner for choosing a club and budget, buying and selling players, rebuilding a squad and sharing the final lineup.',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' }
          })}
        </script>
      </Helmet>

      <div className="ts-shell">

        {/* ── Header ── */}
        <header className="ts-header">
          <Link
            to="/"
            className="ts-logo-link"
            aria-label="Squadverse home"
          >
            <img
              src={process.env.PUBLIC_URL + '/squadverse.png'}
              alt="Squadverse"
              className="ts-logo"
            />
          </Link>

          <nav className="ts-nav" aria-label="Main navigation">
            <Link to="/lineup-builder" className="ts-nav-link">Lineup Builder</Link>
            <Link to="/tactics-board" className="ts-nav-link">Tactics Board</Link>
            <Link to="/38-0" className="ts-nav-link">38-0 Draft</Link>
            <Link to="/world-xi-cup" className="ts-nav-link">World XI Cup</Link>
            <a
              href="mailto:squadverse2025@gmail.com"
              className="ts-nav-link ts-nav-contact"
            >
              Contact Us
            </a>
          </nav>

          <button
            className="ts-menu-toggle"
            aria-expanded={mobileMenuOpen}
            aria-controls="ts-mobile-nav"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </header>

        {mobileMenuOpen && (
          <nav
            id="ts-mobile-nav"
            className="ts-mobile-nav"
            aria-label="Mobile navigation"
          >
            <Link
              to="/lineup-builder"
              className="ts-mobile-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              Lineup Builder
            </Link>
            <Link
              to="/tactics-board"
              className="ts-mobile-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              Tactics Board
            </Link>
            <Link
              to="/38-0"
              className="ts-mobile-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              38-0 Draft
            </Link>
            <Link
              to="/world-xi-cup"
              className="ts-mobile-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              World XI Cup
            </Link>
            <a
              href="mailto:squadverse2025@gmail.com"
              className="ts-mobile-nav-link ts-mobile-nav-contact"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact Us
            </a>
          </nav>
        )}

        {/* ── Hero ── */}
        <section className="ts-hero">
          <span className="ts-badge">Transfer window simulator</span>
          <h1 className="ts-hero-title">Free Football Transfer Builder</h1>
          <p className="ts-hero-copy">
            Choose a team, set your budget, then manage signings, sales and your final squad.
          </p>
          <p className="ts-hero-note">
            Buy, sell, rebuild and share your squad — no sign-up needed.
          </p>
        </section>

        {/* ── Two-column content grid ── */}
        <div className="ts-content-grid">

          {/* Setup card */}
          <section className="ts-setup-card" aria-labelledby="ts-setup-heading">
            <h2 id="ts-setup-heading" className="ts-card-heading">
              Set up your transfer window
            </h2>

            <form onSubmit={handleSubmit} noValidate>

              {/* Step 1 */}
              <div className="ts-step">
                <div className="ts-step-header">
                  <span className="ts-step-num" aria-hidden="true">1</span>
                  <span className="ts-step-label">Choose a team</span>
                </div>
                <TeamSearchInput
                  selectedTeam={selectedTeam}
                  onTeamSelect={handleTeamSelect}
                  isMobile={isMobile}
                  isSmallMobile={isSmallMobile}
                />
                <p className="ts-helper-text">
                  Pick an existing team or create your own.
                </p>
              </div>

              {/* Step 2 */}
              <div className="ts-step">
                <div className="ts-step-header">
                  <span className="ts-step-num" aria-hidden="true">2</span>
                  <span className="ts-step-label">Team name</span>
                </div>
                {selectedTeam && selectedTeam.nation !== 'Custom' ? (
                  <div className="ts-team-pill">
                    <span className="ts-team-pill-name">{selectedTeam.name}</span>
                    <button
                      type="button"
                      className="ts-team-pill-edit"
                      onClick={() => {
                        setCustomTeamName(selectedTeam.name);
                        setSelectedTeam(null);
                      }}
                      aria-label={`Edit team name: currently ${selectedTeam.name}`}
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <>
                    <label htmlFor="ts-custom-name" className="ts-visually-hidden">
                      Custom team name
                    </label>
                    <input
                      id="ts-custom-name"
                      type="text"
                      value={customTeamName}
                      onChange={(e) => setCustomTeamName(e.target.value)}
                      placeholder="e.g., Dream FC, Ultimate United"
                      className="ts-input"
                      autoComplete="off"
                    />
                  </>
                )}
              </div>

              {/* Step 3 */}
              <div className="ts-step">
                <div className="ts-step-header">
                  <span className="ts-step-num" aria-hidden="true">3</span>
                  <span className="ts-step-label">Set your budget</span>
                </div>
                <BudgetInput budget={budget} setBudget={setBudget} isMobile={isMobile} />
              </div>

              {errorMsg && (
                <div className="ts-error" role="alert">
                  {errorMsg}
                </div>
              )}

              <button type="submit" className="ts-cta-btn">
                Start building
              </button>

              {!isFormValid && (
                <p className="ts-cta-hint" aria-live="polite">
                  {!hasTeamName
                    ? 'Select or name a team to continue'
                    : 'Set a budget to continue'}
                </p>
              )}

            </form>
          </section>

          {/* Preview card */}
          <aside className="ts-preview-card" aria-label="Transfer Builder preview">
            <h2 className="ts-card-heading">Your transfer room awaits</h2>

            <div className="ts-preview-stats" aria-hidden="true">
              <div className="ts-stat-tile">
                <span className="ts-stat-label">Budget</span>
                <span className="ts-stat-value ts-stat--green">£200M</span>
              </div>
              <div className="ts-stat-tile">
                <span className="ts-stat-label">Net spend</span>
                <span className="ts-stat-value ts-stat--amber">+£120M</span>
              </div>
              <div className="ts-stat-tile">
                <span className="ts-stat-label">Players in</span>
                <span className="ts-stat-value ts-stat--blue">6</span>
              </div>
              <div className="ts-stat-tile">
                <span className="ts-stat-label">Final squad</span>
                <span className="ts-stat-value">30</span>
              </div>
            </div>

            <div className="ts-mini-pitch" aria-hidden="true">
              <div className="ts-pitch-halfline"></div>
              <div className="ts-pitch-circle"></div>
              <div className="ts-pitch-rows">
                <div className="ts-pitch-row">
                  <span className="ts-dot ts-dot--gk"></span>
                </div>
                <div className="ts-pitch-row">
                  <span className="ts-dot"></span>
                  <span className="ts-dot"></span>
                  <span className="ts-dot"></span>
                  <span className="ts-dot"></span>
                </div>
                <div className="ts-pitch-row">
                  <span className="ts-dot ts-dot--new"></span>
                  <span className="ts-dot"></span>
                  <span className="ts-dot ts-dot--new"></span>
                </div>
                <div className="ts-pitch-row">
                  <span className="ts-dot ts-dot--new"></span>
                  <span className="ts-dot"></span>
                  <span className="ts-dot"></span>
                </div>
              </div>
            </div>

            <ul className="ts-feature-list" aria-label="Transfer Builder features">
              <li className="ts-feature-item">
                <span className="ts-feature-icon" aria-hidden="true">⇅</span>
                Track players in and out
              </li>
              <li className="ts-feature-item">
                <span className="ts-feature-icon" aria-hidden="true">£</span>
                Watch your net spend live
              </li>
              <li className="ts-feature-item">
                <span className="ts-feature-icon" aria-hidden="true">⊕</span>
                Build directly on the tactics board
              </li>
              <li className="ts-feature-item">
                <span className="ts-feature-icon" aria-hidden="true">↗</span>
                Export and share your final squad
              </li>
            </ul>
          </aside>

        </div>

        <section className="ts-seo-copy" aria-labelledby="ts-guide-heading">
          <h2 id="ts-guide-heading">Plan a complete football transfer window</h2>
          <p>
            Start with a real club or create your own team, set the transfer budget and work through each part of
            a squad rebuild. Squadverse keeps your plan local in the browser, so no account is required.
          </p>
          <div className="ts-seo-grid">
            <div>
              <h3>Buy and sell players</h3>
              <p>Search the player market, track arrivals and departures, and see your remaining budget and net spend update as you build.</p>
            </div>
            <div>
              <h3>Rebuild the squad</h3>
              <p>Review the roster, organise your new signings on the tactics board and shape the final squad around your preferred formation.</p>
            </div>
            <div>
              <h3>Save and share your plan</h3>
              <p>Export a designed squad graphic or share the finished transfer window with friends when your rebuild is complete.</p>
            </div>
          </div>
          <p className="ts-seo-next-step">
            Prefer a scored management game? Try the <Link to="/transfer-window-challenge">Transfer Window Challenge</Link> and simulate the season after your business.
          </p>
        </section>

        <AdSense slot={ADSENSE_SLOTS.information} />

        {/* ── Tool switcher ── */}
        <nav className="ts-tool-switcher" aria-label="Squadverse tools">
          <button
            type="button"
            onClick={handleSubmit}
            className="ts-tool-btn ts-tool-active"
            aria-current="page"
          >
            Transfer Builder
          </button>
          <Link to="/lineup-builder" className="ts-tool-btn">Lineup Builder</Link>
          <Link to="/transfer-window-challenge" className="ts-tool-btn">Transfer Window Challenge</Link>
          <Link to="/tactics-board" className="ts-tool-btn">Tactics Board</Link>
          <Link to="/38-0" className="ts-tool-btn">38-0 Draft</Link>
          <Link to="/world-xi-cup" className="ts-tool-btn">World XI Cup</Link>
        </nav>

        {/* ── Footer ── */}
        <footer className="ts-footer">
          <div className="ts-footer-top">
            <div className="ts-footer-brand">
              <img
                src={process.env.PUBLIC_URL + '/squadverse.png'}
                alt=""
                aria-hidden="true"
                className="ts-footer-logo"
              />
              <span className="ts-footer-name">Squadverse</span>
            </div>
            <p className="ts-footer-tagline">Free football tools for fans.</p>
            <nav className="ts-footer-links" aria-label="Footer navigation">
              <a href="mailto:squadverse2025@gmail.com" className="ts-footer-link">
                Contact Us
              </a>
              <a
                href="https://x.com/SquadVerseUK"
                target="_blank"
                rel="noopener noreferrer"
                className="ts-footer-link"
              >
                Follow on X
              </a>
              <a href="/terms-and-conditions" className="ts-footer-link">
                Terms & Conditions
              </a>
            </nav>
            <a
              href="https://buymeacoffee.com/ammarabuyahya"
              target="_blank"
              rel="noopener noreferrer"
              className="ts-footer-support"
            >
              ☕ Support our work
            </a>
          </div>
          <p className="ts-footer-copy">© 2025 Squadverse. All rights reserved.</p>
        </footer>

      </div>
    </main>
  );
}

export default TeamSelection;
